import type { CheckboxProps as MuiCheckboxProps } from '@mui/material/Checkbox';
import type { SxProps, Theme } from '@mui/material/styles';

import MuiFormControlLabel from '@mui/material/FormControlLabel';
import MuiCheckbox from '@mui/material/Checkbox';
import { Skeleton } from '@mui/material';
import Box from '@mui/material/Box';
import React from 'react';

import type { FieldProps } from '../types';

import { useIntersectionObserver } from '../../../hooks';
import { assignRef } from '../../../utils/assignRef';
import { HelperMessage } from '../../HelperMessage';
import { ErrorMessage } from '../../ErrorMessage';

export type CheckboxFieldProps = {
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onIntersection?: () => void;
  defaultValue?: string;
  isObserved?: boolean;
  sx?: SxProps<Theme>;
} & Pick<
  MuiCheckboxProps,
  'checked' | 'classes' | 'defaultChecked' | 'onChange' | 'value'
> &
  FieldProps;

const CheckboxField = React.forwardRef(
  (
    {
      checked,
      className,
      classes,
      'data-testid': testId,
      defaultChecked,
      defaultValue,
      disabled,
      errors,
      helperText,
      isObserved,
      label,
      loading,
      name,
      onChange,
      onClick,
      onIntersection,
      sx,
      value,
    }: CheckboxFieldProps,
    ref: React.ForwardedRef<HTMLButtonElement>,
  ) => {
    const internalRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
      assignRef(ref, internalRef.current);
    }, [ref]);

    useIntersectionObserver({
      isObserved,
      onIntersection,
      ref: internalRef,
    });

    if (loading) {
      return (
        <Skeleton
          sx={{ display: 'flex', flexDirection: 'column', px: 4 }}
          className={className}
          height={42}
        />
      );
    }
    return (
      <Box
        flexDirection="column"
        className={className}
        display="flex"
        sx={sx}
        px={4}
      >
        <MuiFormControlLabel
          control={
            <MuiCheckbox
              defaultChecked={defaultChecked}
              defaultValue={defaultValue}
              data-testid={testId}
              onChange={onChange}
              disabled={disabled}
              onClick={onClick}
              checked={checked}
              classes={classes}
              ref={internalRef}
              value={value}
              name={name}
            />
          }
          sx={{ '&:hover': { bgcolor: 'action.hover' } }}
          disabled={disabled}
          label={label}
        />
        {helperText && (
          <HelperMessage sx={{ pl: '31px', pr: 2 }}>{helperText}</HelperMessage>
        )}
        {errors && (
          <Box pl="10px" pr={2}>
            <ErrorMessage errors={errors} />
          </Box>
        )}
      </Box>
    );
  },
);

CheckboxField.displayName = 'CheckboxField';

export default CheckboxField;
